/**
 * CSS 타입, function 제외
 */
export type StyleData = Pick<
  {
    [P in keyof CSSStyleDeclaration]?: CSSStyleDeclaration[P] | null;
  },
  {
    // eslint-disable-next-line @typescript-eslint/ban-types
    [P in keyof CSSStyleDeclaration]: CSSStyleDeclaration[P] extends Function
      ? never
      : P;
  }[keyof CSSStyleDeclaration]
> & { [key: string]: string };

/**
 * 컬럼 버튼 옵션
 */
export interface ColumnButtonOptions<T = any> {
  index: number;
  text?: string;
  icon?: string;
  className?: string;
  style?: StyleData;
  disabled?: boolean;
  onClick?: (data: T) => void;
}

/**
 * 특정 조건에 따라 Button을 반환하는 callback
 */
export type ColumnButton<T> = (obj: T) => ColumnButtonOptions<T> | null;

/**
 * 버튼 클릭 이벤트
 */
export type ColumnButtonClickEvent<T> = {
  value: T;
  option: ColumnButtonOptions<T>;
};

/**
 * 특정 조건에 따라 value를 반환하는 callback
 */
export type ValueFn<T> = (obj: T) => any;

/**
 * 특정 조건에 따라 style을 반환하는 callback
 */
export type StyleFn<T> = (obj: T) => StyleData | null;

/**
 * 컬럼 설정
 */
export interface DataTableColumnConfig<T> {
  /**
   * 컬럼명 또는 'button' 또는 'icon'
   */
  key: string;

  /**
   * 컬럼 표시명
   */
  name: string;

  /**
   * 컬럼 스타일
   */
  style?: StyleData;

  /**
   * 컬럼 숨김 여부
   */
  hidden?: boolean;

  /**
   * 셀 값 변환 콜백
   */
  valueFn?: ValueFn<T>;

  /**
   * 셀 값 클래스 설정 콜백 — `valueFn` 평문 셀(codeType·translate 없음)에서만 적용된다.
   * 빈 문자열·null 이면 평문, 값이 있으면 셀 값을 그 클래스의 `<span>` 으로 감싼다(예: 'badge text-bg-secondary').
   */
  classFn?: (obj: T) => string | null;

  /**
   * 셀 스타일 설정 콜백
   */
  styleFn?: StyleFn<any>;

  /**
   * 행 스타일 설정 콜백
   */
  styleFnByItem?: StyleFn<T>;

  /**
   * 값별 스타일
   */
  styleMap?: { [value: string | number]: StyleData };

  /**
   * 정렬 가능 여부, false 명시하지 않으면 정렬 가능
   */
  sort?: boolean;

  /**
   * 번역 (ngx-translate) 사용 여부
   */
  translate?: boolean;

  /**
   * locale pipe 에 사용될 키
   */
  localeKey?: string;

  /**
   * 코드마스터 타입, 설정 시 코드 마스터 locale 값 사용
   */
  codeType?: string;

  /**
   * 가운데 정렬
   */
  center?: boolean;

  /**
   * 제목 정렬
   */
  headerAlign?: 'start' | 'center' | 'end';

  /**
   * 스타일
   */
  headerStyle?: string;

  /**
   * 정렬 시 사용할 고정 정렬
   */
  fixedSort?: string;

  /**
   * 모바일(뷰포트 &lt; 768px)에서 컬럼 숨김.
   * 우선 표시할 컬럼만 남기고 나머지에 true 권장.
   */
  hideOnMobile?: boolean;
}

export interface DataTableColumnButtonConfig<T> {
  /**
   * 컬럼 키
   */
  key: string;

  /**
   * 컬럼 표시명
   */
  name: string;

  /**
   * 컬럼 스타일
   */
  style?: StyleData;

  /**
   * 컬럼 숨김 여부
   */
  hidden?: boolean;

  /**
   * 모바일(뷰포트 &lt; 768px)에서 컬럼 숨김
   */
  hideOnMobile?: boolean;

  /**
   * 컬럼 버튼 옵션
   */
  columnButton: ColumnButton<T>;
}

/**
 * 도움말 버튼
 * @param index 버튼 index
 */
export function HelpButtonConfig(
  index: number,
  onClick?: (data: any) => void,
): DataTableColumnButtonConfig<any> {
  return {
    key: 'help',
    name: '',
    style: { position: 'sticky', right: '0' },
    columnButton: (): ColumnButtonOptions => {
      return {
        index,
        icon: 'box-arrow-in-up-right',
        style: { fontSize: '1.25rem' },
        onClick,
      };
    },
  };
}
